/** Meridian public integration SDK. Amounts are raw integer strings; no browser secrets. */
export * from './near.js';
export * from './near-wire.js';
export * from './near-market-data.js';
export type AssetRequestInput = { name: string; sourceChain?: 'solana'; sourceMint?: string; referenceUrl?: string; description?: string };
export type AssetRequest = {
    id: string; name: string; sourceChain: string | null; sourceMint: string | null;
    referenceUrl: string | null; description: string;
    status: 'pending_research' | 'pending_review' | 'reviewed' | 'deferred' | 'rejected';
    createdAt: number; reviewedAt: number | null; reviewNote: string | null;
    reviewDueAt: number | null; reviewOverdue: boolean; voteCount: number; voted: boolean;
    approval: 'not_granted'; reviewCadence: string;
};
export type CostDisclosure = {
    decimals?:number; symbol?:string; rateBps?:number;
    category: 'receipt_purchase' | 'mandatory_platform' | 'creator_tax' | 'dex_lp' | 'transfer' | 'bridge_network' | 'price_impact';
    amount: string | null; denomination: string | null; evidence: string;
    basis?: 'provider_quote' | 'onchain_quote'; includedInQuote?: boolean; quoteExpiresAt?: number | null;
};
export type ExecutionCostEvidence = {
    costs:{stage:string;network:string;transactionHash:string;payer:string;amount:string;denomination:'BNB (wei)'|'SOL (lamports)';outcome:'succeeded'|'failed';observedAt:number}[];
    scope:'recorded_transaction_network_fees_only';protocolMarkup:'0';deducted:boolean;limitations:string;reconciliation?:{actualNativeWei:string;quoteDigest:string;evidenceDigest:string}|null;
};
export type CrossChainQuote={costs?:CostDisclosure[];purchaseFeeBps?:number;purchaseFeeWei?:string;acquisitionNativeWei?:string;id:string;digest:string;wallet:string;assetId:string;kind:'reward_acquisition'|'buyer_acquisition'|'native_exit';amount:string;expectedOut:string;minimumOut:string;intermediateMinimum:string;maximumCostWei:string;estimatedCostWei:string;expiresAt:number;localFixture:boolean;solCostRate:{nativeWei:string;lamports:string};legs:{name:string;input:string;expectedOut:string;minimumOut:string}[];protocolMarkupBps:0;costPolicy:string};
export type ManagedExit = {
    journalState: string;
    phase: 'funds_escrowed' | 'awaiting_native_registration' | 'source_release_pending' | 'sale_pending' | 'return_bridge_pending' | 'destination_payout_pending' | 'completed' | 'recovery_needed' | 'cancelled' | 'cancellation_pending' | 'awaiting_finality' | 'unknown';
    localFixture: boolean; recipient: string; minimumOutput: { amount: string|null; denomination: string };
    actualOutput: { amount: string; denomination: string } | null;
    costs: CostDisclosure[];
    actions: { stage: string; state: string; transactionHash: string | null; updatedAt: number }[];
    recovery: { required: boolean; retryAvailable: boolean; detail: string };
};
export type CapabilityDisclosure = {
    assetRequests: 'implemented'; scheduledAssetReview: false;
    receiptSwaps: 'configured' | 'unconfigured';
    managedCashExit: 'local_fixture' | 'configured' | 'unavailable'; nativeBnbManagedExit: 'local_fixture' | 'configured' | 'unavailable';
    externalOperatorRunner: 'implemented_release_gated'; productionApprovedAssets: 'not_verified';
};
export type EconomicsDisclosure = {
    status: 'confirmed_requirements_not_verified_on_current_deployment';
    target: { amount: string; denomination: 'BNB' };
    migrationFee: { amount: string; denomination: 'BNB'; earnedPolicy: 'unresolved' | 'success_only' };
    acquisitionBudgetBeforeCosts: { amount: string; denomination: 'BNB' };
    mandatoryBps: 150; meridianBps: 100; creatorBps: 30; sharedLiquidityBps: 20;
    optionalCreatorTaxMaxBps: 400; graduationFundedSharedLiquidity: false;
    receiptPurchaseFee?: {bps:200; basis:'entered_bnb'; earned:'receipt_delivery'; refundedOnRecovery:true};
    crossChainExecutionCosts: {payer:'transaction_amount'; protocolMarkupBps:0; estimateBeforeConfirmation:true; protectedMinimumRequired:true; actualCostReconciliationRequired:true};
    limitations: string[];
};
/** Getter observations, not a security audit or evidence that all planned policies exist. */
export type ObservedEconomics = {
    chainId: number; launchpad: string; runtimeCodeHash: string;
    curveFeeBps: number; defaultTargetWei: string; migrationFeeWei: string;
    localFixtureTargets: boolean; migrationFeeDisposition: 'escrow_only_policy_unresolved' | 'earned_on_success';
    rewardPolicyVersion?: number;
    creatorFeeRecipientVersion?: number;
};
export type Asset = {
    id: string;
    symbol: string;
    name: string;
    issuer: string;
    sourceChain: string;
    sourceMint: string;
    receiptAddress: string | null;
    decimals: number;
    status: 'local-fixture' | 'review-required' | 'active' | 'paused';
    description: string;
    riskDisclosure: string;
    vaultBalance: string;
    receiptSupply: string;
};
export type DisplayUsdQuote = {quoteSymbol:string;usd:string;asOf:number;validUntil:number;source:string;sourceUrl?:string};
/** Display-only Solana mainnet reference; fetchedAt is retrieval time, not last trade time. */
export type SourceAssetPrice = {name:string;sourceMint:string;usd:string;change24h:number|null;fetchedAt:number;validUntil:number;sourceUrl:string};
export type Launch = {
    id: string;
    address: string;
    tokenAddress: string;
    name: string;
    symbol: string;
    description: string;
    imageUrl: string | null;
    creator: string;
    creatorFeeRecipient?: string;
    assetId: string;
    assetSymbol: string;
    receiptAddress: string;
    state: 'curve' | 'graduating' | 'live' | 'recovery';
    contractState: number;
    curveSellEnabled: boolean;
    raised: string;
    target: string;
    progress: number;
    tokenReserve: string;
    quoteReserve: string;
    supply: string;
    poolAddress: string | null;
    createdAt: number;
    price: string;
    /** Estimated terminal curve spot price in BNB; null after graduation. */
    graduationPriceBNB?: string | null;
    priceDenomination: string;
    volume24h: string;
    trades24h: number;
};
export type Trade = {
    recipient?: string;
    attribution?: 'curve' | 'router' | 'recipient_only';
    id: string;
    launchId: string;
    wallet: string;
    side: 'buy' | 'sell';
    amountIn: string;
    amountOut: string;
    quoteSymbol: string;
    transactionHash: string;
    blockNumber: number;
    timestamp: number;
};
export type PreparedTransaction = {
    chainId: number;
    to: string;
    data: string;
    value: string;
    description: string;
};
export type PrepareResult = {
    liquidity?:{tokenAmount:string;quoteAmount:string;minimumToken:string;minimumQuote:string;shareAfterPercent:string};
    nativePayout?: {expectedOut:string;minimumOut:string;requiresRegistration:true};
    disclosure?: { outputKind: 'receipt' | 'creator_token' | 'native_bnb' | 'pending_cash_exit' | 'other'; asynchronous: boolean; costs: CostDisclosure[]; tradingFeeBps?: number };
    transactions: PreparedTransaction[];
    expectedOut?: string;
    minimumOut?: string;
    quoteSymbol?: string;
    warnings: string[];
};
export type Settlement = {
    payout?: {kind:'stablecoin'|'native_bnb';decimals:6|18;symbol:string;minimumOut:string|null;intermediateDecimals?:number;fees?:{maximum:string;actual:string;finalized:boolean;refundClaimed:boolean}};
    managedExit?: ManagedExit;
    id: string;
    requestId: string | null;
    canRequestCancellation: boolean;
    wallet: string;
    assetId: string;
    amount: string;
    destination: string;
    state: string;
    mode: string;
    transactionHash: string | null;
    payoutHash: string | null;
    minimumOut: string;
    actualOut: string | null;
    error: string | null;
    createdAt: number;
    updatedAt: number;
    history: {
        state: string;
        at: number;
        detail: string;
    }[];
};
export type PlatformConfig = {
    legacyPortfolioPath?: '/legacy/portfolio';
    legacyDeployment?: boolean;
    observedEconomics?: ObservedEconomics | null;
    capabilities?: CapabilityDisclosure;
    economics?: EconomicsDisclosure;
    hostedPreview?: boolean;
    name: string;
    mode: 'local' | 'testnet' | 'production';
    chainId: number;
    chainName: string;
    rpcUrl: string;
    explorerUrl: string | null;
    nativeSymbol: string;
    authOrigin: string;
    factoryAddress: string | null;
    gatewayAddress: string | null;
    stablecoinAddress: string | null;
    finalityBlocks: number;
    deploymentReady: boolean;
    mainnetReadOnly?: boolean;
    cashSettlementEnabled: boolean;
    localFixture: boolean;
    releaseBlockers: string[];
};
export type Session = {
    address: string;
    chainId: number;
    csrfToken: string;
    expiresAt: number;
};
export type Portfolio = {
    rewardLaunches?: Launch[];
    address: string;
    nativeBalance: string;
    stablecoinBalance: string;
    holdings: {
        launch: Launch;
        balance: string;
    }[];
    receipts: {
        asset: Asset;
        balance: string;
    }[];
};
export type PrepareRequest = {
    action: 'claimLPRewards' | 'addLiquidity' | 'removeLiquidity' | 'acquireReceipts' | 'recoverPurchase' | 'create' | 'buy' | 'sell' | 'redeem' | 'registerNativeExit' | 'claimNativeCostRefund' | 'updateNativeMinimums' | 'cancelRedemption' | 'graduate' | 'claimFees' | 'claimRewards' | 'approve';
    quoteAmount?: string;
    minimumToken?: string;
    minimumQuote?: string;
    address: string;
    launchId?: string;
    assetId?: string;
    amount?: string;
    slippageBps?: number;
    name?: string;
    symbol?: string;
    description?: string;
    destination?: string;
    minimumOut?: string;
    requestId?: string;
    optionalTaxBps?: number;
    creatorFeeRecipient?: string;
    quoteId?:string;
    purchaseRecovery?: 'refund_unspent' | 'refund_converted' | 'recover_exported' | 'claim_cost_refund';
    rewardKind?: 'fallback_bnb' | 'receipts' | 'acquire' | 'refund_acquisition' | 'refund_converted' | 'recover_exported' | 'claim_cost_refund';
};
export type HolderRewardStatus = {
    automation?:{receiptAirdrops:true;curveConversion:'configured'|'unavailable';state?:string;error?:string};
    launchId:string; vault:string|null; taxBps:number; nativeAccrued:string; receiptsAccrued:string;
    fallbackAt:number|null; fallbackAvailable:boolean; graduated:boolean; receiptSymbol:string; receiptDecimals:number;
    conversionAvailable:false;
    acquisition?: {address:string; requests:{purchaseFeeWei?:string;id:string;stage:number;nativeAmount:string;minimum:string;actualReceipts:string;recoveredNative?:string;fees?:{maximum:string;actual:string;finalized:boolean;refundClaimed:boolean};recoveryRequested?:boolean;jobId?:string;executionState?:string;processingIssue?:'operator_signature_required'|'signed_transaction_expired'|'provider_retry'|'provider_unavailable'}[]};
};
export type BuyerAcquisitionStatus = {assetId:string;address:string|null;available:boolean;receiptSymbol:string;receiptDecimals:number;receiptBalance:string;requests:NonNullable<HolderRewardStatus['acquisition']>['requests']};
export type HolderAnalytics = {
    blockNumber:number; blockHash:string; asOf:number; complete:boolean; totalSupply:string;
    holderCount:number; topTenPercent:string; creatorPercent:string; eligibleSupply:string;
    rows:{address:string;balance:string;percent:string;rewardPercent:string|null;kind:'wallet'|'pool'|'launchpad'|'excluded'}[];
};
export type Candle = {time:number;open:string;high:string;low:string;close:string;volume:string;trades:number};
export type MarketHistory = {candles:Candle[];interval:number;quoteSymbol:string;quoteDecimals:number;from:number;to:number;indexedAt:number|null;lastTradeAt:number|null;complete:boolean;priceBasis:string};
export type LiquidityPosition = {pool:string;available:boolean;blockNumber:number;totalSupply:string;balance:string;sharePercent:string;lockedPercent:string;tokenAmount:string;quoteAmount:string;tokenReserve:string;quoteReserve:string;quoteDecimals:number;feeBps:number;feeAprPercent:string|null;aprBasis:string;warnings:string[]};
export type LiquidityIncentives = {available:false;reason:string}|{available:true;policy:string;rewards:string;operator:string;blockNumber:number;phase:'lp_rewards'|'pool_funding';thresholdUsd:string;usdDecimals:number;liquidityUsd:string|null;valuationFresh:boolean;valuationAt:number;valuationUntil:number;valuationEvidence:string;receiptPriceUsd:string;assetQuoteReserves:string;quoteDecimals:number;quoteSymbol:string;eligibleLPSharePercent:string;claimableNative:string;claimableReceipts:string;pendingNative:string;nativeGrowth:string;poolGrowthReceipts:string;acquisitionConfigured:boolean;description:string};
export class ApiError extends Error {
    constructor(public status: number, message: string, public code?: string) { super(message); }
}
export class MeridianClient {
    private csrf = '';
    constructor(public baseUrl = '', private fetcher: typeof fetch = (...args) => globalThis.fetch(...args)) { }
    private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
        const response = await this.fetcher(`${this.baseUrl}/api${path}`, { ...init, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(this.csrf ? { 'x-csrf-token': this.csrf } : {}), ...init.headers } });
        const payload = await response.json();
        if (!response.ok)
            throw new ApiError(response.status, payload.message ?? payload.error ?? 'Request failed', payload.code);
        return payload as T;
    }
    config() { return this.request<PlatformConfig>('/config'); }
    assets() { return this.request<Asset[]>('/assets'); }
    assetRequests(limit = 50, offset = 0) { return this.request<AssetRequest[]>(`/asset-requests?limit=${limit}&offset=${offset}`); }
    assetRequest(id: string) { return this.request<AssetRequest>(`/asset-requests/${encodeURIComponent(id)}`); }
    submitAssetRequest(input: AssetRequestInput) { return this.request<AssetRequest>('/asset-requests', {method: 'POST', body: JSON.stringify(input)}); }
    voteAssetRequest(id: string, voted = true) { return this.request<AssetRequest>(`/asset-requests/${encodeURIComponent(id)}/vote`, {method: 'POST', body: JSON.stringify({voted})}); }
    launches(params: {
        search?: string;
        asset?: string;
        state?: string;
    } = {}) {
        return this.request<Launch[]>(`/launches?${new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined) as [
            string,
            string
        ][])}`);
    }
    launch(id: string) { return this.request<Launch>(`/launches/${encodeURIComponent(id)}`); }
    trades(id: string) { return this.request<Trade[]>(`/launches/${encodeURIComponent(id)}/trades`); }
    holders(id:string){return this.request<HolderAnalytics>(`/launches/${encodeURIComponent(id)}/holders`);}
    candles(id:string,range:'1H'|'24H'|'7D'|'ALL'='24H',interval=300000){return this.request<MarketHistory>(`/launches/${encodeURIComponent(id)}/candles?range=${range}&interval=${interval}`);}
    liquidity(id:string,address?:string){return this.request<LiquidityPosition>(`/launches/${encodeURIComponent(id)}/liquidity${address?`?address=${encodeURIComponent(address)}`:''}`);}
    liquidityIncentives(id:string,address?:string){return this.request<LiquidityIncentives>(`/launches/${encodeURIComponent(id)}/liquidity-incentives${address?`?address=${encodeURIComponent(address)}`:''}`);}
    async session() { const s = await this.request<Session | null>('/auth/session'); this.csrf = s?.csrfToken ?? ''; return s; }
    challenge(address: string, chainId: number) {
        return this.request<{
            id: string;
            message: string;
            expiresAt: number;
        }>('/auth/challenge', { method: 'POST', body: JSON.stringify({ address, chainId }) });
    }
    async verify(id: string, signature: string) { const s = await this.request<Session>('/auth/verify', { method: 'POST', body: JSON.stringify({ id, signature }) }); this.csrf = s.csrfToken; return s; }
    async logout() { await this.request('/auth/logout', { method: 'POST', body: '{}' }); this.csrf = ''; }
    buyerAcquisitions(assetId:string){return this.request<BuyerAcquisitionStatus>(`/buyer-acquisitions/${encodeURIComponent(assetId)}`);}
    retryBuyerAcquisition(id:string){return this.request<{id:string;state:string}>(`/buyer-acquisitions/${encodeURIComponent(id)}/retry`,{method:'POST',body:'{}'});}
    async retryRewardAcquisition(id:string){return this.request<{id:string;state:string}>(`/reward-acquisitions/${encodeURIComponent(id)}/retry`,{method:'POST',body:'{}'});}
    executionCosts(id:string){return this.request<ExecutionCostEvidence>(`/executions/${encodeURIComponent(id)}/costs`);}
    routeQuote(input:{kind:CrossChainQuote['kind'];launchId?:string;assetId?:string;amount?:string}){return this.request<CrossChainQuote>('/route-quotes',{method:'POST',body:JSON.stringify(input)});}
    portfolio(address: string) { return this.request<Portfolio>(`/portfolio/${encodeURIComponent(address)}`); }
    prepare(input: PrepareRequest) { return this.request<PrepareResult>('/transactions/prepare', { method: 'POST', body: JSON.stringify(input) }); }
    quote(input: PrepareRequest) { return this.request<PrepareResult>('/quotes', { method: 'POST', body: JSON.stringify(input) }); }
    track(hash: string) {
        return this.request<{
            state: string;
            confirmations: number;
            hash: string;
            blockNumber?: number;
            error?: string;
        }>(`/transactions/${hash}`);
    }
    settlements() { return this.request<Settlement[]>('/settlements'); }
    settlement(id: string) { return this.request<Settlement>(`/settlements/${encodeURIComponent(id)}`); }
    registerSettlement(input: {
        transactionHash: string;
        assetId: string;
        amount: string;
        destination: string;
        minimumOut: string;
    }) { return this.request<Settlement>('/settlements', { method: 'POST', body: JSON.stringify(input) }); }
    retrySettlement(id: string) { return this.request<Settlement>(`/settlements/${encodeURIComponent(id)}/retry`, { method: 'POST', body: '{}' }); }
    saveMetadata(input: {
        transactionHash: string;
        description: string;
        imageUrl?: string;
    }) { return this.request('/launches/metadata', { method: 'POST', body: JSON.stringify(input) }); }
}
